import { defaultLocale } from "@/i18n/config";

/** Upper bound for a callback URL; anything longer is refused. */
export const MAX_CALLBACK_URL_LENGTH = 2048;

/** Locale-prefixed default target after login. */
export const DEFAULT_CALLBACK_PATH = `/${defaultLocale}/dashboard`;

/**
 * Percent-encoded C0 control characters and DEL (`%0a`, `%09`, `%7f` …).
 * They have no business in a login return target and are refused outright.
 */
const ENCODED_CONTROL_PATTERN = /%(?:[01][0-9a-f]|7f)/i;

function hasUnsafeRawCharacter(raw: string): boolean {
  for (let i = 0; i < raw.length; i += 1) {
    const code = raw.charCodeAt(i);
    // C0 controls, space, DEL and the C1 range: browsers strip or rewrite
    // some of them while parsing ("java\tscript:", "/\t/host" ⇒ "//host").
    if (code <= 0x20 || (code >= 0x7f && code <= 0x9f)) return true;
    // Backslashes are rewritten to slashes by browsers ("/\host" ⇒ "//host").
    if (code === 0x5c) return true;
  }
  return false;
}

/**
 * Turns an untrusted `callbackUrl` (query string, request body) into a safe
 * navigation target of the SAME origin. The result is always a relative path
 * (`pathname + search + hash`); every doubtful value yields `fallback`.
 *
 * Refused: empty or overlong values, any other origin, any scheme other than
 * http/https of the own origin (`javascript:`, `data:`, `vbscript:`, `blob:` …),
 * protocol-relative `//host`, `/\host`, `\\host`, raw or percent-encoded
 * control characters and whitespace, and paths that normalise to `//…`.
 *
 * Never throws.
 */
export function safeCallbackUrl(
  raw: string | null | undefined,
  origin: string,
  fallback: string = DEFAULT_CALLBACK_PATH,
): string {
  try {
    if (typeof raw !== "string") return fallback;
    if (raw.length === 0 || raw.length > MAX_CALLBACK_URL_LENGTH) return fallback;
    if (hasUnsafeRawCharacter(raw)) return fallback;
    if (ENCODED_CONTROL_PATTERN.test(raw)) return fallback;

    let base: URL;
    try {
      base = new URL(origin);
    } catch {
      return fallback;
    }
    if (base.protocol !== "http:" && base.protocol !== "https:") return fallback;

    const isRelative = raw.startsWith("/");
    if (isRelative) {
      // Exactly one leading slash — never "//host" or "/\host".
      if (raw[1] === "/" || raw[1] === "\\") return fallback;
    } else if (!/^https?:\/\//i.test(raw)) {
      // Absolute values must be plain http(s) of the own origin.
      return fallback;
    }

    let url: URL;
    try {
      url = new URL(raw, base.origin);
    } catch {
      return fallback;
    }
    if (url.origin !== base.origin) return fallback;
    if (url.username !== "" || url.password !== "") return fallback;
    // Dot segments can normalise "/.//host" to the pathname "//host", which a
    // browser would read as protocol-relative.
    if (!url.pathname.startsWith("/") || url.pathname.startsWith("//")) {
      return fallback;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
}
